import React from 'react';
import { motion } from 'framer-motion';
import { Users, Award, Heart, Leaf } from 'lucide-react';
import Reviews from "../components/Reviews.js";
import CountUp from 'react-countup';

import image1 from '../assets/images/image1.jpg';
import image2 from '../assets/images/image2.jpg';
import image3 from '../assets/images/image3.jpg';
import imege4 from '../assets/images/imege4.jpg';

const About = () => {
  const stats = [
    { icon: Users, number: "3000+", label: "Clientes Satisfechos" },
    { icon: Award, number: "5+", label: "Años de Experiencia" },
    { icon: Heart, number: "98%", label: "Satisfacción" },
    { icon: Leaf, number: "100%", label: "Calidad" }
  ];

  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4">

        {/* TITULO PRINCIPAL + TEXTO (CENTRADO) */}
        <motion.div
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-green-600 mb-6">
            Donde la medicina y la naturaleza se encuentran
          </h2>

          <p className="text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            En Bienestar al Natural somos un consultorio médico particular ubicado en la ciudad de Medellín,
            donde la medicina alternativa, la estética y el cuidado corporal se unen para brindarte una experiencia
            confiable, efectiva y personalizada, logrando así el perfecto equilibrio entre tu salud y tu belleza.
          </p>
        </motion.div>

        {/* IMÁGENES + TEXTO */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">

          {/* Imágenes */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-2 gap-4">
              <img
                src={image1}
                alt="Equipo Bienestar al Natural"
                className="w-full h-72 object-cover rounded-2xl shadow-lg transition duration-300 hover:scale-105"
              />
              <img
                src={image2}
                alt="Productos naturales"
                className="w-full h-72 object-cover rounded-2xl shadow-lg transition duration-300 hover:scale-105"
              />
              <img
                src={image3}
                alt="Consulta médica"
                className="w-full h-72 object-cover rounded-2xl shadow-lg transition duration-300 hover:scale-105"
              />
              <img
                src={imege4}
                alt="Laboratorio natural"
                className="w-full h-72 object-cover rounded-2xl shadow-lg transition duration-300 hover:scale-105"
              />
            </div>
          </motion.div>

          {/* Texto (IZQUIERDA) */}
          <motion.div
            className="space-y-6 text-left"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-gray-800">
              Nuestro Propósito
            </h3>

            <p className="text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed">
              Creemos que la salud no se trata solo de aliviar síntomas, sino de entender el origen de lo que el cuerpo expresa.
              Nuestro propósito es ayudarte a recuperar tu equilibrio natural a través de terapias que respetan tu biología,
              fortalecen tus defensas y estimulan la capacidad innata del cuerpo para sanar.
            </p>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200">
              <h4 className="font-semibold text-green-800 mb-2 text-lg">
                Nuestros Valores
              </h4>

              <ul className="text-green-700 space-y-1 text-base md:text-lg">
                <li>• Compromiso con la salud natural</li>
                <li>• Productos de alta calidad</li>
                <li>• Atención personalizada</li>
                <li>• Técnicas vanguardistas</li>
              </ul>

              <motion.a
                href="https://wa.me/573237076868"
                className="mt-6 inline-block bg-green-600 text-white px-6 py-3 rounded-full font-semibold shadow-md hover:bg-green-700 transition duration-300 text-sm md:text-base"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                Agenda tu Cita
              </motion.a>
            </div>
          </motion.div>
        </div>

        {/* STATS – CUADROS PEQUEÑOS Y RESPONSIVOS */}
        <motion.div
          className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200 text-center"
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                <stat.icon className="w-6 h-6 text-white" />
              </div>

              <h3 className="text-xl font-bold text-green-600 mb-1">
                {stat.label === "Natural" 
                  ? "100%" 
                  : <>
                      <CountUp
                        end={parseInt(stat.number)}
                        duration={2}
                        start={0}
                        enableScrollSpy={true}
                        scrollSpyOnce={true}
                      />
                      {stat.number.includes('+') ? '+' : stat.number.includes('%') ? '%' : ''}
                    </>
                }
              </h3>

              <p className="text-gray-600 font-medium text-sm sm:text-base">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* REVIEWS */}
        <div className="mt-16">
          <Reviews />
        </div>

      </div>
    </section>
  );
};

export default About;

